/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  useMsal,
  useIsAuthenticated,
  useAccount,
  MsalProvider,
} from '@azure/msal-react';
import {
  PublicClientApplication,
  InteractionStatus,
  EventType,
  type AccountInfo,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { msalConfig, loginRequest, adminApiScopes, b2cPolicies } from './authConfig';
import { decodeJwtClaims, hasAdminRole, hasRoleClaimSource } from './tokenClaims';

// Initialize MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

// Handle redirect promise and set active account
msalInstance.initialize().then(() => {
  msalInstance
    .handleRedirectPromise()
    .then((response) => {
      if (response) {
        msalInstance.setActiveAccount(response.account);
      } else {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0]);
        }
      }
    })
    .catch((error) => {
      if (error?.errorCode !== 'no_token_request_cache_error') {
        console.error('Error handling redirect:', error);
      }
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
      }
    });

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult;
      msalInstance.setActiveAccount(payload.account);
    }
  });
});

export interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  account: AccountInfo;
  isAdmin: boolean;
}

interface AuthContextType {
  user: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isRoleResolved: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: (scopes?: string[]) => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Shared mutex for popup token acquisition
let popupTokenPromise: Promise<string | null> | null = null;

function AuthProviderInner({ children }: { children: ReactNode }) {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount();

  // Fast path: the Admin role claim read synchronously off the AccountInfo
  // snapshot useAccount() returns. Immediately after loginPopup this snapshot
  // is built from the live AuthenticationResult and carries B2C claims. On a
  // COLD LOAD, msal-browser rebuilds it from encrypted localStorage during
  // initialize() and idTokenClaims is only attached once the cached ID token
  // has been matched to the tenant profile — a match that is not guaranteed
  // to have happened yet when this snapshot is taken. See the async fallback
  // below for the case where that match hasn't landed.
  const idTokenClaims = account?.idTokenClaims as Record<string, unknown> | undefined;
  const isAdminFromSnapshot = useMemo(() => hasAdminRole(idTokenClaims), [idTokenClaims]);
  const accountHasRoleSource = useMemo(() => hasRoleClaimSource(idTokenClaims), [idTokenClaims]);

  // Authoritative async fallback, used only when the snapshot carries no
  // role-claim source at all (the cold-load case). Null means "no probe
  // answer yet" — a genuine non-admin snapshot or an unauthenticated visitor
  // never wait on this; both are decided synchronously above.
  const [resolvedIsAdmin, setResolvedIsAdmin] = useState<boolean | null>(null);
  const homeAccountId = account?.homeAccountId;

  // Reset the probe's answer synchronously during render (not in an effect)
  // whenever the account identity changes, so a signed-out/in cycle can never
  // inherit a previous account's resolved answer. This is React's documented
  // "adjusting state when a prop changes" pattern: calling setState here lets
  // React redo this render with the reset value before anything commits,
  // instead of an effect flashing the stale answer for one frame first.
  const [resolvedForAccountId, setResolvedForAccountId] = useState(homeAccountId);
  if (homeAccountId !== resolvedForAccountId) {
    setResolvedForAccountId(homeAccountId);
    setResolvedIsAdmin(null);
  }

  useEffect(() => {
    if (!account || !isAuthenticated || accountHasRoleSource) {
      // Nothing to resolve: no session, or the snapshot already carries a
      // role-claim source and is authoritative — no token call needed. This
      // is what keeps a genuine non-admin off the extra network call.
      return;
    }

    let cancelled = false;
    instance
      .acquireTokenSilent({ scopes: adminApiScopes, account })
      .then((response: AuthenticationResult) => {
        if (cancelled) return;
        const candidates: Array<Record<string, unknown> | null> = [
          (response.idTokenClaims as Record<string, unknown> | undefined) ?? null,
          decodeJwtClaims(response.idToken),
          decodeJwtClaims(response.accessToken),
        ];
        const source = candidates.find((claims) => hasRoleClaimSource(claims));
        setResolvedIsAdmin(hasAdminRole(source ?? null));
      })
      .catch(() => {
        // Every failure path — including interaction_required — resolves to
        // NOT admin. The app must always settle, never hang on Loading.
        if (!cancelled) setResolvedIsAdmin(false);
      });

    return () => {
      cancelled = true;
    };
    // account's snapshot identity changes on every MSAL event — depend on
    // homeAccountId instead so this effect doesn't loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeAccountId, isAuthenticated, accountHasRoleSource, instance]);

  // The snapshot is authoritative the moment it carries a role-claim source —
  // trust it exclusively even if an earlier cold-load probe resolved a (now
  // stale) answer for this same account, e.g. after a background token
  // refresh updates idTokenClaims mid-session. Only fall back to the probe's
  // answer when the snapshot has no role-claim source of its own.
  const effectiveIsAdmin = accountHasRoleSource ? isAdminFromSnapshot : resolvedIsAdmin === true;

  const isRoleResolved =
    !account || !isAuthenticated || accountHasRoleSource || resolvedIsAdmin !== null;

  const user = useMemo<AdminUser | null>(() => {
    if (!account || !isAuthenticated) return null;
    return {
      id: account.localAccountId || account.homeAccountId,
      email: account.username || '',
      displayName: account.name || account.username || 'Admin',
      account,
      isAdmin: effectiveIsAdmin,
    };
  }, [account, isAuthenticated, effectiveIsAdmin]);

  const login = useCallback(async () => {
    try {
      await instance.loginPopup({
        ...loginRequest,
        authority: b2cPolicies.authorities.signUpSignIn.authority,
      });
    } catch (error: unknown) {
      const errorCode = (error as { errorCode?: string })?.errorCode;
      const isPopupBlocked =
        errorCode === 'popup_window_error' || errorCode === 'empty_window_error';
      if (isPopupBlocked) {
        await instance.loginRedirect({
          ...loginRequest,
          authority: b2cPolicies.authorities.signUpSignIn.authority,
        });
      } else {
        throw error;
      }
    }
  }, [instance]);

  const logout = useCallback(async () => {
    try {
      await instance.logoutPopup({
        mainWindowRedirectUri: window.location.origin,
      });
    } catch (error: unknown) {
      const errorCode = (error as { errorCode?: string })?.errorCode;
      const isPopupBlocked =
        errorCode === 'popup_window_error' || errorCode === 'empty_window_error';
      if (isPopupBlocked) {
        await instance.logoutRedirect({
          account: account ?? instance.getActiveAccount() ?? undefined,
          postLogoutRedirectUri: window.location.origin,
        });
        return;
      }
      console.error('Logout error:', error);
      throw error;
    }
  }, [instance, account]);

  const getAccessToken = useCallback(
    async (scopes?: string[]): Promise<string | null> => {
      if (!account) return null;
      const tokenScopes = scopes || adminApiScopes;
      try {
        const response = await instance.acquireTokenSilent({ scopes: tokenScopes, account });
        return response.accessToken;
      } catch {
        if (popupTokenPromise) return popupTokenPromise;
        popupTokenPromise = instance
          .acquireTokenPopup({ scopes: tokenScopes })
          .then((r) => r.accessToken)
          .catch(async (err) => {
            const code = (err as { errorCode?: string })?.errorCode;
            if (code === 'popup_window_error' || code === 'empty_window_error') {
              await instance.acquireTokenRedirect({ scopes: tokenScopes });
              return null;
            }
            return null;
          })
          .finally(() => { popupTokenPromise = null; });
        return popupTokenPromise;
      }
    },
    [instance, account]
  );

  const isLoading = inProgress !== InteractionStatus.None;

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated, isLoading, isRoleResolved, login, logout, getAccessToken }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProviderInner>{children}</AuthProviderInner>
    </MsalProvider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
